import { useState } from 'react';
import { RegisterOptions, useFormContext } from 'react-hook-form';
import Icon from '@/components/common/iconedLinks/Icon';
import { URLS } from '@/constants/urls';

type FormFieldProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'required' | 'pattern'> & {
	label?: string;
	error?: string;
	multiline?: boolean;
	rows?: number;
	required?: RegisterOptions['required'];
	validate?: RegisterOptions['validate'];
	pattern?: RegisterOptions['pattern'];
};

const FormField = ({
	label,
	error,
	type = 'text',
	multiline,
	rows = 4,
	className,
	id,
	name,
	required,
	validate,
	pattern,
	...rest
}: FormFieldProps) => {
	const [isPwdVisible, setIsPwdVisible] = useState(false);
	const displayLabel = label && required ? `${label}*` : label;

	const {
		register,
		formState: { errors },
	} = useFormContext();

	const fieldError = error ?? (errors?.[name]?.message as string);
	const validationClass = fieldError ? ' is-invalid' : '';
	const registerProps = register(name, { required, validate, pattern });

	if (multiline) {
		return (
			<div className={`form-group${validationClass}`}>
				{displayLabel && <label htmlFor={id}>{displayLabel}</label>}
				<textarea
					id={id}
					rows={rows}
					className={`form-control${className ? ' ' + className : ''}${validationClass}`}
					{...(rest as unknown as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
					{...registerProps}
				/>
				{fieldError && (
					<div className="invalid-feedback" data-test-id="invalid-feedback">
						{fieldError}
					</div>
				)}
			</div>
		);
	}

	return (
		<div className={`form-group${validationClass}`}>
			{displayLabel && <label htmlFor={id}>{displayLabel}</label>}
			{type === 'password' ? (
				<div className="input-group">
					<input
						data-test-id="input-text"
						id={id}
						type={isPwdVisible ? 'text' : 'password'}
						className={`form-control${className ? ' ' + className : ''}${validationClass}`}
						{...rest}
						{...registerProps}
					/>
					<div className="visibility-button input-group-append" onClick={() => setIsPwdVisible((v) => !v)}>
						<span className="input-group-text">
							<Icon
								src={isPwdVisible ? URLS.ICON_HIDDEN : URLS.ICON_VIEW}
								alt={isPwdVisible ? 'hidden' : 'view'}
								height={20}
								width={20}
							/>
						</span>
					</div>
				</div>
			) : (
				<input
					data-test-id="input-text"
					id={id}
					type={type}
					className={`form-control${className ? ' ' + className : ''}${validationClass}`}
					{...rest}
					{...registerProps}
				/>
			)}
			{fieldError && (
				<div className="invalid-feedback" data-test-id="invalid-feedback">
					{fieldError}
				</div>
			)}
		</div>
	);
};

FormField.displayName = 'FormField';
export default FormField;
